/**
 * CareLogForm Component
 *
 * Form for creating or editing care log entries.
 * Includes text area, tag checkboxes, and optional date picker.
 */

import { useState } from 'react';
import { Calendar, Save, X } from 'lucide-react';

export const CARE_LOG_TAGS = [
  { value: 'medical', label: 'Medical' },
  { value: 'school', label: 'School' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'admin', label: 'Admin' },
  { value: 'behavior', label: 'Behavior' },
  { value: 'medication', label: 'Medication' },
] as const;

export type CareLogTag = (typeof CARE_LOG_TAGS)[number]['value'];

interface CareLogFormProps {
  initialText?: string;
  initialDate?: string;
  initialTags?: CareLogTag[];
  onSubmit: (data: { logText: string; logDate: string; tags: CareLogTag[] }) => Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
  isLoading?: boolean;
}

function getTodayDateString(): string {
  const now = new Date();
  return now.toISOString().split('T')[0];
}

export function CareLogForm({
  initialText = '',
  initialDate,
  initialTags,
  onSubmit,
  onCancel,
  submitLabel = 'Save Entry',
  isLoading = false,
}: CareLogFormProps): JSX.Element {
  const [logText, setLogText] = useState(initialText);
  const [logDate, setLogDate] = useState(initialDate || getTodayDateString());
  const [selectedTags, setSelectedTags] = useState<CareLogTag[]>(initialTags || []);
  const [error, setError] = useState<string | null>(null);

  const handleTagToggle = (tag: CareLogTag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedText = logText.trim();
    if (!trimmedText) {
      setError('Please enter some text for your care log.');
      return;
    }

    if (trimmedText.length > 10000) {
      setError('Care log text cannot exceed 10,000 characters.');
      return;
    }

    try {
      await onSubmit({
        logText: trimmedText,
        logDate,
        tags: selectedTags,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save care log');
    }
  };

  const characterCount = logText.length;
  const isOverLimit = characterCount > 10000;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Text Area */}
      <div>
        <label htmlFor="logText" className="label">
          What happened today?
        </label>
        <textarea
          id="logText"
          name="logText"
          value={logText}
          onChange={(e) => setLogText(e.target.value)}
          placeholder="Record observations, conversations, medications given, behaviors noticed..."
          className={`w-full px-3 py-2 bg-white text-ink border border-soft-neutral rounded-md font-body text-base placeholder:text-muted min-h-[150px] resize-y focus:border-evergreen focus:ring-2 focus:ring-evergreen/20 focus:outline-none ${isOverLimit ? 'border-red-500' : ''}`}
          disabled={isLoading}
        />
        <div className={`text-xs mt-1 text-right ${isOverLimit ? 'text-red-500' : 'text-muted'}`}>
          {characterCount.toLocaleString()} / 10,000 characters
        </div>
      </div>

      {/* Date Picker */}
      <div>
        <label htmlFor="logDate" className="label flex items-center gap-1">
          <Calendar size={14} />
          Date
        </label>
        <input
          id="logDate"
          name="logDate"
          type="date"
          value={logDate}
          onChange={(e) => setLogDate(e.target.value)}
          max={getTodayDateString()}
          className="px-3 py-2 bg-white text-ink border border-soft-neutral rounded-md font-body text-base focus:border-evergreen focus:ring-2 focus:ring-evergreen/20 focus:outline-none"
          disabled={isLoading}
        />
      </div>

      {/* Tag Checkboxes */}
      <div>
        <span className="block mb-1.5 font-body font-medium text-sm text-ink">Tags (optional)</span>
        <div className="flex flex-wrap gap-2">
          {CARE_LOG_TAGS.map((tag) => (
            <button
              key={tag.value}
              type="button"
              onClick={() => !isLoading && handleTagToggle(tag.value)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-colors ${
                selectedTags.includes(tag.value)
                  ? 'bg-evergreen text-white border-evergreen'
                  : 'bg-white text-ink border-soft-neutral hover:border-evergreen'
              } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              disabled={isLoading}
            >
              {tag.label}
            </button>
          ))}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 justify-end pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary flex items-center gap-2"
            disabled={isLoading}
          >
            <X size={16} />
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="btn-primary flex items-center gap-2"
          disabled={isLoading || isOverLimit || !logText.trim()}
        >
          {isLoading ? (
            <>
              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              Saving...
            </>
          ) : (
            <>
              <Save size={16} />
              {submitLabel}
            </>
          )}
        </button>
      </div>
    </form>
  );
}

export default CareLogForm;
